import { useState, useEffect, useMemo } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { useBuyer } from "../contexts/BuyerContext";
import Header from "../components/Header";
import { OrderListSkeleton } from "../components/Skeleton";
import "./Prepartion.css";
import { useLocation } from "react-router-dom";

const Prepartion = () => {
  const { buyer } = useBuyer();
  const location = useLocation();

  const serviceType = location.state?.serviceType || null;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  const config = useMemo(
    () => ({
      status: "Preparation",
      title: "Buyer Confirmation",
    }),
    []
  );

  // --------------------------------------------------
  // STATUS COLOR
  // --------------------------------------------------

  const getStatusColor = (status) => {
    switch (status) {
      case "Preparation":
        return "orange";

      default:
        return "gray";
    }
  };

  // --------------------------------------------------
  // FETCH ORDERS
  // --------------------------------------------------

  useEffect(() => {
    const fetchOrders = async () => {
      const buyerId = buyer?.buyerId;

      if (!buyerId || !serviceType) {
        setOrders([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const ordersRef = collection(db, "orderList");

        const q = query(
          ordersRef,
          where("buyerId", "==", buyerId),
          where("serviceType", "==", serviceType),
          where("orderStatus", "==", "Preparation")
        );
        const snapshot = await getDocs(q);

        /*
         * IMPORTANT
         *
         * Store the complete Firestore document
         * reference together with its data.
         *
         * docSnap.id = actual Firestore document ID
         * docSnap.ref = actual Firestore document reference
         */

        const fetchedOrders = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();

          return {
            ...data,

            // Actual Firestore document ID
            documentId: docSnap.id,

            // Actual Firestore document reference
            documentRef: docSnap.ref,
          };
        });

        console.log(
          "Fetched orders:",
          fetchedOrders.map((order) => ({
            documentId: order.documentId,
            path: order.documentRef.path,
            buyerId: order.buyerId,
            orderStatus: order.orderStatus,
          }))
        );

        setOrders(fetchedOrders);
      } catch (err) {
        console.error(
          "Error fetching preparation orders:",
          err
        );

        setError(
          `Failed to load preparation orders: ${err.message}`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [buyer?.buyerId, serviceType]);

  // --------------------------------------------------
  // CONFIRM ORDER
  // --------------------------------------------------

  const handleConfirmOrder = async (order) => {
    /*
     * DO NOT reconstruct the document reference.
     *
     * Use the reference that came directly from
     * Firestore during the fetch.
     */

    const orderRef = order.documentRef;

    if (!orderRef) {
      alert("Firestore document reference is missing.");
      return;
    }

    if (order.buyerId !== buyer?.buyerId) {
      alert("You are not authorized to confirm this order.");
      return;
    }

    if (order.orderStatus !== "Preparation") {
      alert("This order is no longer in Preparation.");
      return;
    }

    if (order.buyerConfirmation === true) {
      return;
    }

    try {
      setUpdatingOrderId(order.documentId);

      console.log("Updating Firestore document:", {
        id: order.documentId,
        path: orderRef.path,
      });

      await updateDoc(orderRef, {
        buyerConfirmation: true,
        buyerConfirmedAt: serverTimestamp(),
      });

      setOrders((prevOrders) =>
        prevOrders.map((item) =>
          item.documentId === order.documentId
            ? {
              ...item,
              buyerConfirmation: true,
            }
            : item
        )
      );

      console.log(
        "Order confirmed:",
        order.documentId
      );
    } catch (err) {
      console.error(
        "Error confirming order:",
        err
      );

      alert(
        `Failed to confirm order: ${err.message}`
      );
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // --------------------------------------------------
  // REJECT ORDER
  // --------------------------------------------------

  const handleRejectOrder = async (order) => {
    const orderRef = order.documentRef;

    if (!orderRef) {
      alert("Firestore document reference is missing.");
      return;
    }

    if (order.buyerId !== buyer?.buyerId) {
      alert("You are not authorized to reject this order.");
      return;
    }

    if (order.orderStatus !== "Preparation") {
      alert("This order is no longer in Preparation.");
      return;
    }

    try {
      setUpdatingOrderId(order.documentId);

      await updateDoc(orderRef, {
        orderStatus: "Rejected",
        buyerConfirmation: false,
        buyerConfirmedAt: null,
      });

      setOrders((prevOrders) =>
        prevOrders.map((item) =>
          item.documentId === order.documentId
            ? {
              ...item,
              orderStatus: "Rejected",
              buyerConfirmation: false,
              buyerConfirmedAt: null,
            }
            : item
        )
      );
    } catch (err) {
      console.error("Error rejecting order:", err);
      alert(`Failed to reject order: ${err.message}`);
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // --------------------------------------------------
  // CONFIRM BUTTON
  // --------------------------------------------------

  const ConfirmButton = ({ order }) => {
    const confirmed =
      order.buyerConfirmation === true;

    const updating =
      updatingOrderId === order.documentId;

    return (
      <button
        type="button"
        onClick={() => handleConfirmOrder(order)}
        disabled={updating || confirmed}
        className="confirm-btn"
      >
        {updating
          ? "Confirming..."
          : confirmed
            ? "Confirmed"
            : "Confirm"}
      </button>
    );
  };

  // --------------------------------------------------
  // REJECT BUTTON
  // --------------------------------------------------

  const RejectButton = ({ order }) => {
    const updating =
      updatingOrderId === order.documentId;

    return (
      <button
        type="button"
        onClick={() => handleRejectOrder(order)}
        disabled={updating}
        style={{
          backgroundColor: "#ef4444",
          color: "white",
          border: "none",
          padding: "6px 12px",
          borderRadius: "4px",
          cursor: updating ? "not-allowed" : "pointer",
          fontSize: "14px",
          fontWeight: "500",
          opacity: updating ? 0.6 : 1,
          transition: "opacity 0.2s ease",
          marginLeft: "8px",
        }}
      >
        {updating ? "Rejecting..." : "Reject"}
      </button>
    );
  };

  // --------------------------------------------------
  // LOADING
  // --------------------------------------------------

  if (loading) {
    return (
      <div className="orders-container">
        <Header title={config.title} />
        <OrderListSkeleton count={3} />
      </div>
    );
  }

  // --------------------------------------------------
  // ERROR
  // --------------------------------------------------

  if (error) {
    return (
      <div className="orders-container">
        <Header title={config.title} />

        <div className="error-state">
          <p className="error">{error}</p>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="retry-btn"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // --------------------------------------------------
  // UI
  // --------------------------------------------------

  return (
    <>
      <Header title={config.title} />

      <div className="orders-header">
        <p>
          {orders.length} order
          {orders.length !== 1 ? "s" : ""} found
        </p>
      </div>

      {orders.length === 0 ? (
        <div className="empty-state">
          <p>No Preparation orders found.</p>

          <p className="debug-info">
            Buyer ID: {buyer?.buyerId || "Not found"} |
            Status: {config.status} |
            Service Type: {serviceType || "Not found"}
          </p>
        </div>
      ) : (
        <div className="final-correction-container">
          {orders.map((order) => {
            const totalFineWt =
              parseFloat(order.netWt) || 0;

            const totalMC =
              parseFloat(order.netGramMc) || 0;

            const imageUrl =
              Array.isArray(order.images) &&
                order.images.length > 0
                ? order.images[0]?.url
                : null;

            const confirmed =
              order.buyerConfirmation === true;

            return (
              <div
                key={order.documentId}
                className="ordercard"
              >
                {/* IMAGE */}

                <div className="orderimage">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={
                        order.productName ||
                        "Product"
                      }
                      loading="lazy"
                    />
                  ) : (
                    <div className="image-placeholder">
                      No Image
                    </div>
                  )}
                </div>

                {/* PRODUCT INFORMATION */}

                <div className="select-product-info">
                  <span className="select-product-specification">
                    {order.category || "-"} /
                    {order.productName || "-"}
                  </span>

                  <p className="select-product-name">
                    {order.specification || "-"} /
                    {order.styleType || "-"}
                  </p>

                  <span
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: "500",
                    }}
                  >
                    {order.serviceType
                      ? order.serviceType.toUpperCase()
                      : "-"}
                  </span>

                  {/* VARIANTS */}

                  {Array.isArray(
                    order.selectedVariants
                  ) &&
                    order.selectedVariants.length >
                    0 && (
                      <div className="variant-display">
                        {order.selectedVariants.map(
                          (variant, index) => (
                            <span
                              key={`${variant.size}-${index}`}
                              className="variant-info"
                            >
                              {variant.size} x{" "}
                              {variant.quantity}

                              {index <
                                order.selectedVariants
                                  .length -
                                1
                                ? ", "
                                : ""}
                            </span>
                          )
                        )}
                      </div>
                    )}

                  {/* WEIGHT */}

                  <p className="select-product-weight">
                    Fine Wt. ~
                    {totalFineWt.toFixed(3)}
                    gm
                  </p>

                  {/* AMOUNT */}

                  <p className="select-product-weight">
                    Amount : ~₹{" "}
                    {totalMC.toFixed(2)}
                  </p>

                  {/* ORDER STATUS */}

                  <div
                    className={`status-badge status-${getStatusColor(
                      order.orderStatus
                    )}`}
                  >
                    <span>
                      {order.orderStatus}
                    </span>
                  </div>

                  {/* BUYER CONFIRMATION */}

                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    <span
                      style={{
                        color: confirmed
                          ? "#16a34a"
                          : "#f59e0b",
                      }}
                    >
                      {confirmed
                        ? "Confirmed"
                        : "Pending"}
                    </span>
                  </div>
                </div>

                {/* ACTIONS */}

                <div className="action-btn">
                  {order.orderStatus ===
                    "Preparation" && (
                      <>
                        <ConfirmButton
                          order={order}
                        />

                        <RejectButton
                          order={order}
                        />
                      </>
                    )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default Prepartion;